import { useEffect, useState } from "react";
import axios from "axios";
import { Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public-facing maintenance gate.
 *
 * Wraps every PUBLIC route (Home, About, FAQ, Privacy, Disclaimer, Contact).
 * When `/api/maintenance-status` returns `{enabled: true}`, this replaces the
 * child page with a full-screen "under maintenance" message. Admin routes
 * (/admin, /lw5g-secure-panel-9421) are NOT wrapped, so they always remain
 * accessible.
 *
 * The status is polled every 20 seconds so that flipping OFF from the admin
 * panel restores the site for all visitors within ~20 seconds without any
 * server restart. Database, results, and background jobs are untouched.
 */
export default function MaintenanceGate({ children }) {
  const [state, setState] = useState({ loading: true, enabled: false });

  useEffect(() => {
    let mounted = true;

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/maintenance-status`);
        if (mounted) {
          setState({ loading: false, enabled: !!res.data?.enabled });
        }
      } catch (e) {
        // On error, fail OPEN — never lock out users because the status
        // check failed. Log for debugging.
        console.error("maintenance-status fetch failed:", e);
        if (mounted) setState({ loading: false, enabled: false });
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 20000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  if (state.loading) return null;
  if (!state.enabled) return children;

  return (
    <div className="maintenance-screen" data-testid="maintenance-screen">
      <div className="maintenance-card">
        <div className="maintenance-icon-wrap">
          <Wrench size={48} strokeWidth={1.75} />
        </div>
        <h1 className="maintenance-title" data-testid="maintenance-title">
          Under Maintenance
        </h1>
        <p className="maintenance-message" data-testid="maintenance-message">
          Website is temporarily under maintenance. Please check again shortly.
        </p>
        <div className="maintenance-brand">LuckyWin5G</div>
      </div>
    </div>
  );
}
